/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-use-before-define */
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import React from 'react';
import styled from 'styled-components';

import type { GoalOption } from './GoalSelector';

interface GoalFilterComponentProps {
  goals: GoalOption[];
  selectedGoal: GoalOption | null;
  setSelectedGoal: (goal: GoalOption | null) => void;
  useGoalFilter: boolean;
  setUseGoalFilter: (useFilter: boolean | ((prev: boolean) => boolean)) => void;
  allowNone?: boolean;
  title?: string;
  align?: 'center' | 'left';
}

const GoalFilterComponent: React.FC<GoalFilterComponentProps> = ({
  goals,
  selectedGoal,
  setSelectedGoal,
  useGoalFilter,
  setUseGoalFilter,
  allowNone = true,
  title = 'Target Goal',
  align = 'center',
}) => {
  const handleGoalClick = (goal: GoalOption) => {
    // If clicking the already selected goal, deselect it (if allowNone is true)
    if (selectedGoal?.id === goal.id && allowNone) {
      setSelectedGoal(null);
    } else {
      setSelectedGoal(goal);
    }
  };

  return (
    <GoalFilterSection data-testid="clip-producer-target-goal">
      <CheckBoxContainer
        onClick={() => setUseGoalFilter(prev => !prev)}
        disabled={goals.length === 0}
        align={align}
      >
        <CheckBox isChecked={useGoalFilter} role="button">
          {useGoalFilter && <CheckIconAlt />}
        </CheckBox>
        <div className="checkbox-text">{title}</div>
      </CheckBoxContainer>

      {useGoalFilter && goals.length > 0 && (
        <>
          <HelperText align={align}>Select one</HelperText>
          <GoalGrid>
            {goals.map(goal => {
              const isSelected = selectedGoal?.id === goal.id;
              return (
                <GoalItem
                  key={goal.id}
                  selected={isSelected}
                  onClick={() => handleGoalClick(goal)}
                >
                  <RadioIndicator selected={isSelected}>
                    {isSelected && <RadioDot />}
                  </RadioIndicator>
                  <GoalLabel>{goal.label}</GoalLabel>
                </GoalItem>
              );
            })}
          </GoalGrid>
        </>
      )}
    </GoalFilterSection>
  );
};

export default GoalFilterComponent;

// Styled components
const GoalFilterSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckBoxContainer = styled.div<{
  disabled: boolean;
  align: 'center' | 'left';
}>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.align};
  gap: 8px;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};

  .checkbox-text {
    color: var(--foreground);
    font-size: var(--text-xs);
    line-height: 140%;
    text-align: ${props => props.align};
  }
`;

const CheckBox = styled.div<{ isChecked: boolean }>`
  background-color: ${props =>
    props.isChecked ? 'var(--accent)' : 'transparent'};
  width: 16px;
  height: 16px;
  border-radius: 2px;
  overflow: hidden;
  border: 1px solid
    ${props => (props.isChecked ? 'var(--accent)' : 'var(--text-muted)')};
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GoalGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const GoalItem = styled.div<{ selected: boolean }>`
  padding: 8px 12px;
  border-radius: 8px;
  box-sizing: border-box;
  border: 1px solid var(--border);
  background-color: var(--surface);
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: var(--surface-elevated);
    border-color: var(--border);
  }
`;

const GoalLabel = styled.span`
  color: var(--foreground);
  font-size: var(--text-xs);
  /* Normal, per the doc's "row titles are normal weight, not medium" — at 500
     these option rows read heavier than the section label above them. */
  font-weight: var(--font-weight-normal);
  line-height: 1.3;
  flex: 1;
`;

const HelperText = styled.div<{ align: 'center' | 'left' }>`
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 140%;
  text-align: ${props => props.align};
`;

const RadioIndicator = styled.div<{ selected: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 14px;
  border: 1px solid
    ${props => (props.selected ? 'var(--accent)' : 'var(--text-muted)')};
  background: ${props =>
    props.selected ? 'var(--background)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RadioDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
`;
